import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-toastify';
import { fivemLogAPI } from '../services/api';
import FiveMLogTable, { TABLE_META } from '../components/FiveMLogTable';
import { FiSearch, FiRefreshCw, FiDatabase } from 'react-icons/fi';
import '../styles/Logs.css';

const ALL_TABLES = [
    { key: 'all', label: 'Tất Cả', icon: '📋' },
    ...Object.entries(TABLE_META).map(([key, m]) => ({ key, label: m.label, icon: m.icon })),
];

const getDefaultDates = () => ({ startDate: '', endDate: '' });

const Logs = () => {
    const [logs, setLogs]       = useState([]);
    const [loading, setLoading] = useState(true);
    const [pagination, setPagination] = useState({ page: 1, limit: 50, total: 0, pages: 0 });
    const [activeTable, setActiveTable] = useState('all');
    const [activeCategory, setActiveCategory] = useState(''); // sub-filter cho lệnh-admin
    const [searchInput, setSearchInput] = useState('');
    const [search, setSearch]   = useState('');
    const [dates, setDates]     = useState(getDefaultDates());
    const [tableCounts, setTableCounts] = useState({});
    const [adminSummary, setAdminSummary] = useState(null);

    const fetchLogs = useCallback(async (page = 1) => {
        try {
            setLoading(true);
            const res = await fivemLogAPI.getLogs({
                table: activeTable,
                page,
                limit: 50,
                search,
                startDate: dates.startDate || undefined,
                endDate:   dates.endDate   || undefined,
                category:  activeCategory || undefined,
            });
            setLogs(res.data.logs || []);
            setPagination(res.data.pagination || { page: 1, limit: 50, total: 0, pages: 0 });
        } catch (err) {
            console.error(err);
            toast.error('Không thể tải logs FiveM');
        } finally {
            setLoading(false);
        }
    }, [activeTable, search, dates, activeCategory]);

    useEffect(() => { fetchLogs(1); }, [fetchLogs]);

    // Fetch table counts for tab badges
    useEffect(() => {
        fivemLogAPI.getSummary()
            .then(res => {
                const map = {};
                (res.data.tables || []).forEach(t => { map[t._id] = t.count; });
                map.all = res.data.total || 0;
                setTableCounts(map);
            })
            .catch(() => {});
    }, []);

    // Fetch tổng kết khi vào tab Admin / Lệnh Admin
    const showAdminSummary = activeTable === 'rvn_admin_logs' || activeTable === 'rvn_lenh_admin_logs';
    useEffect(() => {
        if (!showAdminSummary) { setAdminSummary(null); return; }
        fivemLogAPI.getAdminSummary({ days: 7 })
            .then(res => setAdminSummary(res.data))
            .catch(() => setAdminSummary(null));
    }, [showAdminSummary, activeTable]);

    const handleSearch = (e) => {
        e.preventDefault();
        setSearch(searchInput);
    };

    const handleTableChange = (key) => {
        setActiveTable(key);
        setActiveCategory(''); // reset sub-filter khi đổi tab
    };

    // Sub-filter chỉ hiện khi đang ở tab Lệnh Admin
    const showCategoryFilter = activeTable === 'rvn_lenh_admin_logs';
    const CATEGORY_OPTIONS = [
        { k: '',           label: 'Tất cả',       icon: '📋' },
        { k: 'coin',       label: 'Coin/Tiền',    icon: '🪙' },
        { k: 'permission', label: 'Cấp Quyền',    icon: '🛡️' },
        { k: 'unban',      label: 'Unban',        icon: '🔓' },
        { k: 'item',       label: 'Item',         icon: '📦' },
    ];

    // Sub-filter cho tab Vật Phẩm — phân loại theo loại thao tác inventory
    const showItemSubtypeFilter = activeTable === 'rvn_item_logs';
    const ITEM_SUBTYPE_OPTIONS = [
        { k: '',             label: 'Tất Cả',        icon: '📋' },
        { k: 'drop',         label: 'Vứt Đất',       icon: '⬇️' },
        { k: 'pickup_drop',  label: 'Nhặt Đất',      icon: '⬆️' },
        { k: 'trunk_in',     label: 'Bỏ Cốp Sau',    icon: '🚗' },
        { k: 'trunk_out',    label: 'Lấy Cốp Sau',   icon: '🚙' },
        { k: 'glovebox_in',  label: 'Bỏ Cốp Trước',  icon: '🧰' },
        { k: 'glovebox_out', label: 'Lấy Cốp Trước', icon: '🧰' },
        { k: 'give',         label: 'Đưa Người',     icon: '🎁' },
        { k: 'stash_in',     label: 'Bỏ Tủ',         icon: '🗃️' },
        { k: 'stash_out',    label: 'Lấy Tủ',        icon: '🗃️' },
        { k: 'add',          label: 'Thêm (HT)',     icon: '➕' },
        { k: 'remove',       label: 'Xóa (HT)',      icon: '➖' },
    ];

    const handleDateChange = (field, val) => {
        setDates(prev => ({ ...prev, [field]: val }));
    };

    const handleToday = () => {
        setDates(getDefaultDates());
    };

    const handle7days = () => {
        const end = new Date();
        const start = new Date(end.getTime() - 7 * 24 * 60 * 60 * 1000);
        setDates({
            startDate: start.toISOString().slice(0, 16),
            endDate:   end.toISOString().slice(0, 16),
        });
    };

    const handleAllTime = () => {
        setDates({ startDate: '', endDate: '' });
    };

    return (
        <div className="logs-page">
            {/* Header */}
            <div className="logs-header">
                <div>
                    <h1 className="logs-fivem-title">
                        <FiDatabase className="logs-fivem-icon" />
                        Tất Cả Logs
                    </h1>
                    <p className="logs-subtitle">Dữ liệu realtime từ FiveM · Cập nhật mỗi 5 phút</p>
                </div>
                <button className="logs-refresh-btn" onClick={() => fetchLogs(1)} disabled={loading}>
                    <FiRefreshCw className={loading ? 'spinning' : ''} />
                    <span>Làm mới</span>
                </button>
            </div>

            {/* Table filter tabs */}
            <div className="logs-table-tabs">
                {ALL_TABLES.map(t => (
                    <button
                        key={t.key}
                        className={`logs-tab ${activeTable === t.key ? 'active' : ''}`}
                        onClick={() => handleTableChange(t.key)}
                    >
                        <span>{t.icon}</span>
                        <span>{t.label}</span>
                        {tableCounts[t.key] != null && (
                            <span className="logs-tab-count">{tableCounts[t.key].toLocaleString()}</span>
                        )}
                    </button>
                ))}
            </div>

            {/* Tổng kết Admin (cấp quyền + addcoin + namechange + unban) — bấm để filter */}
            {showAdminSummary && adminSummary && (() => {
                const cards = [
                    { k: 'coin',       icon: '🪙', label: 'Add Coin',  count: adminSummary.categories?.coin?.count       || 0, unit: 'lệnh', sub: `+${(adminSummary.totalCoin || 0).toLocaleString('vi-VN')} coin` },
                    { k: 'permission', icon: '🛡️', label: 'Cấp Quyền', count: adminSummary.categories?.permission?.count || 0, unit: 'lượt' },
                    { k: 'unban',      icon: '🔓', label: 'Unban',     count: adminSummary.categories?.unban?.count      || 0, unit: 'vụ' },
                    { k: 'namechange', icon: '💠', label: 'Đổi Tên',   count: adminSummary.namechange || 0,                    unit: 'lần' },
                    { k: '',           icon: '📋', label: 'Tổng Admin',count: adminSummary.total || 0,                         unit: 'hành động', total: true },
                ];
                return (
                    <div className="admin-summary-card">
                        <div className="admin-summary-title">
                            📊 Tổng kết hoạt động Admin (7 ngày)
                            <span className="admin-summary-hint">— bấm để lọc</span>
                        </div>
                        <div className="admin-summary-grid">
                            {cards.map(c => (
                                <button
                                    key={c.k || 'all'}
                                    className={`admin-stat ${c.total ? 'admin-stat-total' : ''} ${activeCategory === c.k ? 'active' : ''}`}
                                    onClick={() => setActiveCategory(c.k)}
                                    type="button"
                                >
                                    <div className="admin-stat-icon">{c.icon}</div>
                                    <div className="admin-stat-body">
                                        <div className="admin-stat-label">{c.label}</div>
                                        <div className="admin-stat-value">
                                            {c.count} <span className="admin-stat-unit">{c.unit}</span>
                                        </div>
                                        {c.sub && <div className="admin-stat-sub">{c.sub}</div>}
                                    </div>
                                </button>
                            ))}
                        </div>
                    </div>
                );
            })()}

            {/* Sub-filter chỉ cho tab Lệnh Admin */}
            {showCategoryFilter && (
                <div className="logs-subfilter-bar">
                    <span className="logs-subfilter-label">Phân loại:</span>
                    {CATEGORY_OPTIONS.map(o => (
                        <button
                            key={o.k}
                            className={`logs-subfilter-btn ${activeCategory === o.k ? 'active' : ''}`}
                            onClick={() => setActiveCategory(o.k)}
                        >
                            <span>{o.icon}</span>
                            <span>{o.label}</span>
                        </button>
                    ))}
                </div>
            )}

            {/* Sub-filter cho tab Vật Phẩm */}
            {showItemSubtypeFilter && (
                <div className="logs-subfilter-bar">
                    <span className="logs-subfilter-label">Loại thao tác:</span>
                    {ITEM_SUBTYPE_OPTIONS.map(o => (
                        <button
                            key={o.k}
                            className={`logs-subfilter-btn ${activeCategory === o.k ? 'active' : ''}`}
                            onClick={() => setActiveCategory(o.k)}
                        >
                            <span>{o.icon}</span>
                            <span>{o.label}</span>
                        </button>
                    ))}
                </div>
            )}

            {/* Filters */}
            <div className="logs-filters">
                <form className="logs-search-form" onSubmit={handleSearch}>
                    <div className="logs-search-wrap">
                        <FiSearch className="logs-search-icon" />
                        <input
                            className="logs-search-input"
                            placeholder="Tìm tên, CitizenID, PID:141, ID:31, hành động..."
                            value={searchInput}
                            onChange={e => setSearchInput(e.target.value)}
                        />
                    </div>
                    <button type="submit" className="logs-search-btn">Tìm</button>
                    {search && (
                        <button type="button" className="logs-clear-btn"
                            onClick={() => { setSearchInput(''); setSearch(''); }}>
                            Xóa
                        </button>
                    )}
                </form>

                <div className="logs-date-filters">
                    <input type="datetime-local" className="logs-date-input"
                        value={dates.startDate} onChange={e => handleDateChange('startDate', e.target.value)} />
                    <span className="logs-date-sep">→</span>
                    <input type="datetime-local" className="logs-date-input"
                        value={dates.endDate}   onChange={e => handleDateChange('endDate',   e.target.value)} />
                    <div className="logs-date-presets">
                        <button type="button" className="logs-preset-btn" onClick={handleToday}>Hôm nay</button>
                        <button type="button" className="logs-preset-btn" onClick={handle7days}>7 ngày</button>
                        <button type="button" className="logs-preset-btn" onClick={handleAllTime}>Tất cả</button>
                    </div>
                </div>
            </div>

            <FiveMLogTable
                logs={logs}
                loading={loading}
                pagination={pagination}
                onPageChange={fetchLogs}
                table={activeTable}
            />
        </div>
    );
};

export default Logs;
